import React, { useEffect, useRef, useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'build', label: 'Build a Map' },
  { key: 'freq', label: 'Frequency Counter' },
  { key: 'twosum', label: 'Two Sum' },
  { key: 'group', label: 'Group Anagrams' },
  { key: 'quiz', label: 'Quick Quiz' },
];

/* ─── Java code shown alongside the animation, per mode/step ───────── */
const codeFor = (mode, ctx) => {
  if (mode === 'build') {
    if (ctx.action === 'put') return { lines: [`map.put("${ctx.key}", ${ctx.value});`], active: 0 };
    if (ctx.action === 'get') return { lines: [`map.get("${ctx.key}");`, `// -> ${ctx.found ? ctx.value : 'null'}`], active: 0 };
    if (ctx.action === 'remove') return { lines: [`map.remove("${ctx.key}");`], active: 0 };
    return { lines: ['Map<String, Integer> map = new HashMap<>();', '// put, get, or remove a key above'], active: -1 };
  }
  if (mode === 'freq') {
    return {
      lines: [
        'Map<Character, Integer> freq = new HashMap<>();',
        'for (char c : s.toCharArray()) {',
        '    freq.put(c, freq.getOrDefault(c, 0) + 1);',
        '}',
      ],
      active: ctx.started ? 2 : 0,
    };
  }
  if (mode === 'twosum') {
    return {
      lines: [
        'Map<Integer, Integer> indexOf = new HashMap<>();',
        'for (int i = 0; i < nums.length; i++) {',
        '    int complement = target - nums[i];',
        '    if (indexOf.containsKey(complement)) {',
        '        return new int[] { indexOf.get(complement), i };',
        '    }',
        '    indexOf.put(nums[i], i);',
        '}',
      ],
      active: ctx.line,
    };
  }
  if (mode === 'group') {
    return {
      lines: [
        'Map<String, List<String>> groups = new HashMap<>();',
        'for (String word : words) {',
        '    char[] chars = word.toCharArray();',
        '    Arrays.sort(chars);',
        '    String signature = new String(chars);',
        '    groups.computeIfAbsent(signature, k -> new ArrayList<>()).add(word);',
        '}',
      ],
      active: ctx.started ? 5 : 0,
    };
  }
  return { lines: [], active: -1 };
};

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const Chip = ({ children, tone = 'default', flash }) => {
  const bg = tone === 'active' ? `linear-gradient(135deg,${V1},${V2})`
    : tone === 'secondary' ? `linear-gradient(135deg,${TEAL},#2563EB)`
    : tone === 'good' ? `linear-gradient(135deg,${GREEN},#27AE60)`
    : 'var(--bg3)';
  const color = tone === 'default' ? 'var(--text)' : '#fff';
  const border = tone === 'active' ? V1 : tone === 'secondary' ? TEAL : tone === 'good' ? GREEN : 'var(--border)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9,
      background: bg, color, border: `1.5px solid ${border}`, fontSize: 13, fontWeight: 700,
      fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap', transition: 'all 0.2s ease',
      transform: flash ? 'scale(1.08)' : 'scale(1)',
    }}>{children}</span>
  );
};

const smallInput = {
  width: 92, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const label = text => <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 64 }}>{text}</span>;

/* ─── Main playground ────────────────────────────────────────────── */
const HashPlayground = () => {
  const [mode, setMode] = useState('build');

  /* Build mode */
  const [entries, setEntries] = useState([]); // [{key, value}]
  const [putKey, setPutKey] = useState('');
  const [putValue, setPutValue] = useState('');
  const [getKey, setGetKey] = useState('');
  const [getResult, setGetResult] = useState(null); // { found, value } | null
  const [flashKey, setFlashKey] = useState(null);
  const [lastAction, setLastAction] = useState({ action: null });
  const flashTimer = useRef(null);

  const doFlash = k => {
    setFlashKey(k);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashKey(null), 650);
  };
  const putEntry = () => {
    if (!putKey.trim() || putValue.trim() === '' || isNaN(Number(putValue))) return;
    const v = Number(putValue);
    setEntries(prev => {
      const idx = prev.findIndex(e => e.key === putKey);
      if (idx === -1) return [...prev, { key: putKey, value: v }];
      const next = [...prev]; next[idx] = { key: putKey, value: v }; return next;
    });
    doFlash(putKey);
    setLastAction({ action: 'put', key: putKey, value: v });
    setPutKey(''); setPutValue('');
  };
  const doGet = () => {
    if (!getKey.trim()) return;
    const found = entries.find(e => e.key === getKey);
    setGetResult({ found: !!found, value: found ? found.value : null });
    setLastAction({ action: 'get', key: getKey, found: !!found, value: found ? found.value : null });
    if (found) doFlash(getKey);
  };
  const doRemove = () => {
    if (!getKey.trim()) return;
    setEntries(prev => prev.filter(e => e.key !== getKey));
    setLastAction({ action: 'remove', key: getKey });
    setGetResult(null);
  };

  /* Frequency mode */
  const [freqInput, setFreqInput] = useState('banana');
  const [freqSteps, setFreqSteps] = useState(null);
  const [freqStep, setFreqStep] = useState(0);
  const [freqPlaying, setFreqPlaying] = useState(false);

  const startFreq = () => {
    const chars = freqInput.replace(/\s/g, '').split('');
    if (chars.length === 0) return;
    const steps = [];
    const map = {};
    chars.forEach((c, i) => {
      map[c] = (map[c] || 0) + 1;
      steps.push({ i, c, map: { ...map } });
    });
    setFreqSteps(steps);
    setFreqStep(0);
  };
  useEffect(() => {
    if (!freqPlaying || !freqSteps) return;
    if (freqStep >= freqSteps.length - 1) { setFreqPlaying(false); return; }
    const t = setTimeout(() => setFreqStep(s => s + 1), 700);
    return () => clearTimeout(t);
  }, [freqPlaying, freqStep, freqSteps]);

  /* Two Sum mode */
  const [tsArray, setTsArray] = useState('3, 4, 5, 6');
  const [tsTarget, setTsTarget] = useState('10');
  const [tsSteps, setTsSteps] = useState(null);
  const [tsStep, setTsStep] = useState(0);

  const startTwoSum = () => {
    const nums = tsArray.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
    const target = parseInt(tsTarget, 10);
    if (nums.length < 2 || isNaN(target)) return;
    const steps = [];
    const map = {};
    for (let i = 0; i < nums.length; i++) {
      const complement = target - nums[i];
      const found = Object.prototype.hasOwnProperty.call(map, complement);
      steps.push({ i, value: nums[i], complement, mapBefore: { ...map }, found, matchIndex: found ? map[complement] : null });
      if (found) break;
      map[nums[i]] = i;
    }
    setTsSteps({ nums, steps });
    setTsStep(0);
  };

  /* Group Anagrams mode */
  const [groupInput, setGroupInput] = useState('eat, tea, tan, ate, nat, bat');
  const [groupSteps, setGroupSteps] = useState(null);
  const [groupStep, setGroupStep] = useState(0);

  const startGroup = () => {
    const words = groupInput.split(',').map(w => w.trim()).filter(Boolean);
    if (words.length === 0) return;
    const steps = [];
    const groups = {};
    words.forEach(word => {
      const sig = word.split('').sort().join('');
      groups[sig] = groups[sig] || [];
      groups[sig].push(word);
      steps.push({ word, sig, groups: JSON.parse(JSON.stringify(groups)) });
    });
    setGroupSteps(steps);
    setGroupStep(0);
  };

  /* Quiz mode */
  const [quizKey, setQuizKey] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });

  const newQuiz = () => {
    if (entries.length === 0) return;
    const pick = entries[Math.floor(Math.random() * entries.length)];
    setQuizKey(pick.key); setQuizAnswer(''); setQuizResult(null);
  };
  const checkQuiz = () => {
    if (quizAnswer.trim() === '' || isNaN(Number(quizAnswer))) return;
    const entry = entries.find(e => e.key === quizKey);
    const correct = entry && Number(quizAnswer) === entry.value;
    setQuizResult(correct ? 'correct' : 'wrong');
    setQuizScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
  };

  let code = { lines: [], active: -1 }, codeTitle = 'Java';
  if (mode === 'build') { code = codeFor('build', lastAction); codeTitle = 'Java — Build a Map'; }
  if (mode === 'freq') { code = codeFor('freq', { started: !!freqSteps }); codeTitle = 'Java — Frequency Counter'; }
  if (mode === 'twosum' && tsSteps) {
    const cur = tsSteps.steps[tsStep];
    code = codeFor('twosum', { line: cur.found ? 3 : 6 });
    codeTitle = 'Java — Complement Lookup';
  }
  if (mode === 'group') { code = codeFor('group', { started: !!groupSteps }); codeTitle = 'Java — Group by Signature'; }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button key={m.key} onClick={() => setMode(m.key)} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
            background: mode === m.key ? V1 : 'transparent',
            color: mode === m.key ? '#fff' : 'var(--muted)',
            fontFamily: "'Satoshi','DM Sans',sans-serif",
          }}>{m.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
        <div>
          {mode === 'build' && (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18, minHeight: 42 }}>
                {entries.length === 0 && <span style={{ fontSize: 13, color: 'var(--muted)' }}>Map is empty — put a key/value pair below.</span>}
                {entries.map(e => (
                  <Chip key={e.key} tone={flashKey === e.key ? 'good' : getResult && getResult.found && getKey === e.key ? 'active' : 'default'} flash={flashKey === e.key}>
                    {e.key} → {e.value}
                  </Chip>
                ))}
              </div>
              <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -10, marginBottom: 18 }}>Shown in insertion order for clarity — a real HashMap gives no ordering guarantee.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {label('Put')}
                  <input value={putKey} onChange={e => setPutKey(e.target.value)} style={smallInput} placeholder="key" />
                  <input value={putValue} onChange={e => setPutValue(e.target.value)} style={smallInput} placeholder="value" />
                  <button onClick={putEntry} style={smallBtn(V1)}>Put</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {label('Get / Remove')}
                  <input value={getKey} onChange={e => { setGetKey(e.target.value); setGetResult(null); }} style={smallInput} placeholder="key" />
                  <button onClick={doGet} style={smallBtn(TEAL)}>Get</button>
                  <button onClick={doRemove} style={smallBtn(RED)}>Remove</button>
                </div>
                {getResult && (
                  <div style={{
                    fontSize: 13, fontWeight: 700, padding: '9px 14px', borderRadius: 9,
                    background: getResult.found ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
                    color: getResult.found ? '#1e9e57' : RED,
                  }}>
                    {getResult.found ? `map.get("${getKey}") → ${getResult.value}` : `map.get("${getKey}") → null (key not present)`}
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'freq' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                {label('Text')}
                <input value={freqInput} onChange={e => setFreqInput(e.target.value)} style={wideInput} placeholder="e.g. banana" />
                <button onClick={startFreq} style={smallBtn(V1)}>Build Frequency Map</button>
              </div>

              {freqSteps && (
                <>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {freqInput.replace(/\s/g, '').split('').map((c, i) => (
                      <Chip key={i} tone={i === freqStep ? 'active' : i < freqStep ? 'default' : 'default'}>{c}</Chip>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {Object.entries(freqSteps[freqStep].map).map(([c, n]) => (
                      <Chip key={c} tone="secondary">{c}: {n}</Chip>
                    ))}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
                    Reading '{freqSteps[freqStep].c}' → freq.put('{freqSteps[freqStep].c}', {freqSteps[freqStep].map[freqSteps[freqStep].c]}).
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => setFreqStep(s => Math.max(0, s - 1))} disabled={freqStep === 0} style={ghostBtn(freqStep === 0)}>← Prev</button>
                    <button onClick={() => setFreqStep(s => Math.min(freqSteps.length - 1, s + 1))} disabled={freqStep === freqSteps.length - 1} style={smallBtn(V1, freqStep === freqSteps.length - 1)}>Next →</button>
                    <button onClick={() => setFreqPlaying(p => !p)} style={smallBtn(freqPlaying ? RED : GREEN)}>{freqPlaying ? 'Pause' : 'Auto Play'}</button>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>Step {freqStep + 1} of {freqSteps.length}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {mode === 'twosum' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {label('Array')}
                  <input value={tsArray} onChange={e => setTsArray(e.target.value)} style={wideInput} placeholder="e.g. 3, 4, 5, 6" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {label('Target')}
                  <input value={tsTarget} onChange={e => setTsTarget(e.target.value)} style={smallInput} placeholder="10" />
                  <button onClick={startTwoSum} style={smallBtn(V1)}>Start</button>
                </div>
              </div>

              {tsSteps && (() => {
                const cur = tsSteps.steps[tsStep];
                return (
                  <>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                      {tsSteps.nums.map((n, i) => (
                        <Chip key={i} tone={cur.found && (i === cur.i || i === cur.matchIndex) ? 'good' : i === cur.i ? 'active' : 'default'}>{n}</Chip>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                      {Object.keys(cur.mapBefore).length === 0
                        ? <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>map is empty so far</span>
                        : Object.entries(cur.mapBefore).map(([k, v]) => <Chip key={k} tone="secondary">{k} → {v}</Chip>)}
                    </div>
                    <div style={{
                      padding: '12px 16px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.6, marginBottom: 16,
                      background: cur.found ? 'rgba(46,204,113,0.1)' : `${V1}0c`,
                      border: `1px solid ${cur.found ? 'rgba(46,204,113,0.35)' : `${V1}25`}`,
                      color: cur.found ? '#1e9e57' : 'var(--text)', fontWeight: cur.found ? 700 : 500,
                    }}>
                      {cur.found
                        ? `Looking for ${tsTarget} − ${cur.value} = ${cur.complement} — found at index ${cur.matchIndex}! Answer: [${cur.matchIndex}, ${cur.i}]`
                        : `Looking for ${tsTarget} − ${cur.value} = ${cur.complement} — not in the map yet. Insert ${cur.value} → index ${cur.i}.`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={() => setTsStep(s => Math.max(0, s - 1))} disabled={tsStep === 0} style={ghostBtn(tsStep === 0)}>← Prev</button>
                      <button onClick={() => setTsStep(s => Math.min(tsSteps.steps.length - 1, s + 1))} disabled={tsStep === tsSteps.steps.length - 1} style={smallBtn(V1, tsStep === tsSteps.steps.length - 1)}>Next →</button>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Step {tsStep + 1} of {tsSteps.steps.length}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {mode === 'group' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                {label('Words')}
                <input value={groupInput} onChange={e => setGroupInput(e.target.value)} style={{ ...wideInput, width: 280 }} placeholder="e.g. eat, tea, tan" />
                <button onClick={startGroup} style={smallBtn(V1)}>Group</button>
              </div>

              {groupSteps && (() => {
                const cur = groupSteps[groupStep];
                return (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                      {Object.entries(cur.groups).map(([sig, words]) => (
                        <div key={sig} style={{
                          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                          padding: '10px 12px', borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)',
                        }}>
                          <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', fontFamily: 'monospace' }}>"{sig}"</span>
                          {words.map((w, i) => (
                            <Chip key={i} tone={w === cur.word && sig === cur.sig ? 'good' : 'default'}>{w}</Chip>
                          ))}
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
                      "{cur.word}" sorted → signature "{cur.sig}" — grouped with any earlier word sharing that signature.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={() => setGroupStep(s => Math.max(0, s - 1))} disabled={groupStep === 0} style={ghostBtn(groupStep === 0)}>← Prev</button>
                      <button onClick={() => setGroupStep(s => Math.min(groupSteps.length - 1, s + 1))} disabled={groupStep === groupSteps.length - 1} style={smallBtn(V1, groupStep === groupSteps.length - 1)}>Next →</button>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Step {groupStep + 1} of {groupSteps.length}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {mode === 'quiz' && (
            <div>
              {entries.length === 0 ? (
                <p style={{ fontSize: 13.5, color: 'var(--muted)' }}>Go to "Build a Map" and put a few key/value pairs first, then come back here to quiz yourself.</p>
              ) : quizKey === null ? (
                <button onClick={newQuiz} style={smallBtn(V1)}>Start Quiz</button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>What does map.get("{quizKey}") return?</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input value={quizAnswer} onChange={e => setQuizAnswer(e.target.value)} style={smallInput} placeholder="your answer" disabled={quizResult !== null} />
                    {quizResult === null
                      ? <button onClick={checkQuiz} style={smallBtn(V1)}>Check</button>
                      : <button onClick={newQuiz} style={smallBtn(TEAL)}>Next Question</button>}
                  </div>
                  {quizResult && (
                    <div style={{
                      fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 9,
                      background: quizResult === 'correct' ? 'rgba(46,204,113,0.12)' : 'rgba(240,65,85,0.1)',
                      color: quizResult === 'correct' ? '#1e9e57' : RED,
                    }}>
                      {quizResult === 'correct' ? 'Correct!' : `Not quite — map.get("${quizKey}") is ${entries.find(e => e.key === quizKey)?.value}.`}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {quizScore.correct} / {quizScore.total}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <CodePanel title={codeTitle} lines={code.lines} active={code.active} />
      </div>

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default HashPlayground;
